// @file Composable for undoable actions
import {
  ActionHistoryType,
  recordRedoActionHistory,
  redo,
  undo,
  type ActionHistoryRecord,
  type SectionActionHistoryData,
} from '@@/bits/action_history_manager'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { n__, __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { DraftPost, Post, Section } from '@@/types'
import KeyboardShortcutTooltip from '@@/vuecomponents/KeyboardShortcutTooltip.vue'
import { useKeyboardShortcutsForUndoRedo } from '@@/vuecomposables/keyboard_shortcuts'
import { ref } from 'vue'

export const DELETE_SNACKBAR_TIMEOUT = 5000
const UNDO_TEXT = __('Undo')
const UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS = ['Ctrl', 'Z']
const UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC = ['⌘', 'Z']

const getUndoDeleteShortcut = (): string[] | undefined => {
  if (!device.desktop) {
    return undefined
  }
  return device.windows ? UNDO_DELETE_ACTION_TEXT_DESKTOP_WINDOWS : UNDO_DELETE_ACTION_TEXT_DESKTOP_MAC
}
const activeSnackbarUids = ref<Record<string, string>>({})

export const useUndoable = (): {
  registerKeyboardShortcuts: () => void
  extractSectionDataForUndoRedo: (section: Section, posts: Post[]) => SectionActionHistoryData
  setDeletedSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
  setRestoredSnackbar: (
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ) => void
} => {
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  async function handleUndo(event: KeyboardEvent): Promise<void> {
    // Don't trigger undo if the user has an active draft
    if (surfaceDraftsStore.isAnyDraftActive) return

    const action = undo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        void surfaceDraftsStore.undoDeleteDrafts(action.data)
        setRestoredSnackbar(ActionHistoryType.DELETE_DRAFTS, action.id, action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.undoDeletePosts(action.data)
        setRestoredSnackbar(ActionHistoryType.DELETE_POSTS, action.id, action.data)
        break
      case ActionHistoryType.DELETE_SECTIONS:
        if (isAppUsing('undoRedoDeleteSectionActions')) {
          await surfaceSectionsStore.undoDeleteSections(action.data)
          setRestoredSnackbar(ActionHistoryType.DELETE_SECTIONS, action.id, action.data)
        }
        break
    }
  }

  function handleRedo(event: KeyboardEvent): void {
    // We don't check for active draft here unlike handleUndo because the user might want to redo their restored draft that is active
    const action = redo()
    if (action == null) return

    // If the user is on a browser default undoable action (e.g. text input, reopen closed tab), they would trigger
    // both that undoable action and the undoable action we track in our stack
    event.preventDefault()
    event.stopPropagation()

    switch (action.type) {
      case ActionHistoryType.DELETE_DRAFTS:
        void surfaceDraftsStore.redoDeleteDrafts(action.data, { actionHistoryId: action.id })
        setDeletedSnackbar(ActionHistoryType.DELETE_DRAFTS, action.id, action.data)
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.redoDeletePosts(action.data)
        recordRedoActionHistory(ActionHistoryType.DELETE_POSTS, action.data, action.id)
        setDeletedSnackbar(ActionHistoryType.DELETE_POSTS, action.id, action.data)
        break
    }
  }

  function registerKeyboardShortcuts(): void {
    useKeyboardShortcutsForUndoRedo({
      handleUndo,
      handleRedo,
    })
  }

  function extractSectionDataForUndoRedo(section: Section, posts: Post[]): SectionActionHistoryData {
    return {
      // only save the section attributes because the section id will be recreated
      sectionAttributes: {
        title: section.title,
        sort_index: section.sort_index,
        color: section.color,
      },
      posts,
    }
  }

  const getDeletedSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft deleted', '%{count} drafts deleted', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post deleted', '%{count} posts deleted', count, { count })
      case ActionHistoryType.DELETE_SECTIONS:
        return n__('Section deleted', '%{count} sections deleted', count, { count })
      default:
        return ''
    }
  }

  const getRestoredSnackbarMessage = (actionType: ActionHistoryType, count: number): string => {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        return n__('Draft restored', '%{count} drafts restored', count, { count })
      case ActionHistoryType.DELETE_POSTS:
        return n__('Post restored', '%{count} posts restored', count, { count })
      case ActionHistoryType.DELETE_SECTIONS:
        return n__('Section restored', '%{count} sections restored', count, { count })
      default:
        return ''
    }
  }

  async function handleUndoDeleteSnackbarAction(
    actionType: ActionHistoryType,
    data: ActionHistoryRecord['data'],
  ): Promise<void> {
    switch (actionType) {
      case ActionHistoryType.DELETE_DRAFTS:
        surfaceDraftsStore.handleUndoDeleteSpecificDrafts(data as DraftPost[])
        break
      case ActionHistoryType.DELETE_POSTS:
        surfacePostsStore.handleUndoDeleteSpecificPosts(data as Post[])
        break
      case ActionHistoryType.DELETE_SECTIONS:
        await surfaceSectionsStore.handleUndoDeleteSpecificSections(data as SectionActionHistoryData[])
        break
      default:
    }
  }

  function setDeletedSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: UNDO_TEXT,
        actionTextActions: [async () => await handleUndoDeleteSnackbarAction(actionType, data)],
        actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
        actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getDeletedSnackbarMessage(actionType, data.length),
        actionText: UNDO_TEXT,
        actionTextActions: [async () => await handleUndoDeleteSnackbarAction(actionType, data)],
        actionContentSlot: device.desktop ? KeyboardShortcutTooltip : undefined,
        actionContentSlotProps: { shortcut: getUndoDeleteShortcut() },
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  function setRestoredSnackbar(
    actionType: ActionHistoryType,
    actionHistoryId: string,
    data: ActionHistoryRecord['data'],
  ): void {
    let snackbarTriggerUid: string
    if (
      activeSnackbarUids.value[actionHistoryId] != null &&
      globalSnackbarStore.isSnackbarBeingShown(activeSnackbarUids.value[actionHistoryId])
    ) {
      snackbarTriggerUid = activeSnackbarUids.value[actionHistoryId]
      globalSnackbarStore.updateSnackbar(snackbarTriggerUid, {
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    } else {
      snackbarTriggerUid = globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: getRestoredSnackbarMessage(actionType, data.length),
        actionText: undefined,
        actionTextActions: [],
        timeout: DELETE_SNACKBAR_TIMEOUT,
      })
    }
    activeSnackbarUids.value[actionHistoryId] = snackbarTriggerUid
  }

  return {
    registerKeyboardShortcuts,
    extractSectionDataForUndoRedo,
    setDeletedSnackbar,
    setRestoredSnackbar,
  }
}
